import { Icons, Link } from '@cannect/new-components/atoms'
import { FaFacebookF, FaInstagram, FaLinkedin, FaTwitter } from 'react-icons/fa6'
import { usePublicRender } from 'utils/renderActions'

const APP_LANDING_URL = 'https://atendimento.cannect.life/app-cannect'
const CONTACT_EMAIL = 'faleconosco@cannect.com.br'

type TFooterLink = { title: string; path: string; external?: boolean }
type TFooterColumn = { title: string; width: string; items: TFooterLink[] }

/**
 * Colunas do footer do Novo Marketplace (Figma node 4161:23924).
 * As larguras vêm do design; abaixo de `md` as colunas empilham.
 *
 * Os cinco itens de "Produtos e serviços" apontam todos para /produtos: o protótipo pressupõe
 * navegação por categoria, mas o helper de filtro (`_buildFilterUrl`) só existe comentado no
 * código, então não há rota de categoria para linkar.
 */
const FOOTER_COLUMNS: TFooterColumn[] = [
  {
    title: 'Para você',
    width: 'md:w-[177px]',
    items: [
      { title: 'Encontrar meu caminho', path: '/triagem' },
      { title: 'Explorar por necessidade', path: '/tratamentos' },
      { title: 'Agendar consulta', path: '/agendamento' },
      { title: 'Minha conta', path: '/painel-do-usuario' }
    ]
  },
  {
    title: 'Produtos e serviços',
    width: 'md:w-[159px]',
    items: [
      { title: 'Medicamentos', path: '/produtos' },
      { title: 'Cannabis medicinal', path: '/produtos' },
      { title: 'Suplementos e cogumelos', path: '/produtos' },
      { title: 'Higiene e beleza', path: '/produtos' },
      { title: 'Mamãe e bebê', path: '/produtos' }
    ]
  },
  {
    title: 'Cuidado contínuo',
    width: 'md:w-[166px]',
    items: [
      { title: 'Cannect Cuida', path: '/cuida' },
      { title: 'Renovação e recompra', path: '/conta?pagina=pedidos-todos' },
      { title: 'Meus pedidos', path: '/conta?pagina=pedidos-todos' },
      { title: 'Aplicativo Cannect', path: APP_LANDING_URL, external: true }
    ]
  },
  {
    title: 'Institucional',
    width: 'md:w-[157px]',
    items: [
      { title: 'Conteúdos', path: 'https://blog.cannect.life', external: true },
      { title: 'Profissionais de saúde', path: '/area-profissionais' },
      { title: 'Sobre a Cannect', path: '/sobre-nos' },
      { title: 'Ajuda e suporte', path: '/perguntas-frequentes' },
      { title: 'Termos, privacidade e segurança', path: '/termos-de-uso' }
    ]
  }
]

const SOCIAL_LINKS = [
  { label: 'Facebook', href: 'https://www.facebook.com/cannect.life/', Icon: FaFacebookF },
  { label: 'Instagram', href: 'https://www.instagram.com/cannect.life/', Icon: FaInstagram },
  { label: 'Twitter', href: 'https://twitter.com/CannectLife', Icon: FaTwitter },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/cannect-saude/', Icon: FaLinkedin }
]

const HEADING_CLASSES = 'text-base font-semibold leading-[1.6] text-neutral-50'
const LINK_CLASSES =
  'text-sm leading-[1.4] text-neutral-50 no-underline transition-opacity hover:opacity-70 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-50 focus-visible:ring-offset-2 focus-visible:ring-offset-marketplace-1000'

const FooterLinkColumn = ({ title, width, items }: TFooterColumn) => (
  <nav aria-label={title} className={`flex w-full flex-col gap-[26px] ${width}`}>
    <h2 className={HEADING_CLASSES}>{title}</h2>
    <ul className="m-0 flex list-none flex-col gap-4 p-0">
      {items.map((item) => (
        <li key={`${title}-${item.title}`}>
          <Link
            to={item.path}
            isExternal={item.external}
            target={item.external ? '_blank' : undefined}
            rel={item.external ? 'noopener noreferrer' : undefined}
            className={LINK_CLASSES}>
            {item.title}
          </Link>
        </li>
      ))}
    </ul>
  </nav>
)

function Footer() {
  const { isPublic } = usePublicRender()

  return (
    <footer id="footer-home" className="bg-marketplace-1000 w-full text-neutral-50">
      <div className="mx-auto w-full max-w-[1230px] px-8">
        <div className="flex flex-col gap-12 pb-16 pt-24 md:flex-row md:items-start md:justify-between md:gap-8">
          <div className="flex w-full flex-col gap-[26px] md:w-[254px]">
            <Link to="/" aria-label="Cannect — página inicial" className="w-fit">
              <Icons.LogoWhite width={128} height={22} />
            </Link>

            <p className="m-0 text-sm leading-[22.27px] text-neutral-50">
              Saúde digital que reúne orientação, profissionais, tratamentos e acompanhamento, para que o cuidado não
              recomece a cada etapa.
            </p>

            <div className="flex flex-col gap-[26px]">
              <div className="flex flex-col gap-4">
                <h2 className={HEADING_CLASSES}>Fale conosco:</h2>
                {/* O protótipo traz este e-mail tachado (token Caption Regular Strikethrough) e grafado
                    "falaconosco@". Ambos são erros do arquivo: sem tachado e com a grafia do código. */}
                <a href={`mailto:${CONTACT_EMAIL}`} className={`${LINK_CLASSES} !text-xs`}>
                  {CONTACT_EMAIL}
                </a>
              </div>

              <div className="flex flex-col gap-4">
                <h2 className={HEADING_CLASSES}>Redes sociais:</h2>
                <ul className="m-0 flex list-none items-center gap-4 p-0">
                  {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                    <li key={label}>
                      <a
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={label}
                        className="focus-visible:ring-offset-marketplace-1000 flex size-[25px] items-center justify-center rounded-full border border-solid border-neutral-50 text-neutral-50 transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-50 focus-visible:ring-offset-2">
                        {/* 14px em círculo de 25px reproduz a proporção glifo/círculo do design. */}
                        <Icon className="h-3.5 w-3.5" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {!isPublic && FOOTER_COLUMNS.map((column) => <FooterLinkColumn key={column.title} {...column} />)}
        </div>

        <div className="border-0 border-t border-solid border-neutral-800 pb-12 pt-8">
          <p className="m-0 text-xs leading-[22.27px] text-neutral-50">
            © {new Date().getFullYear()} Cannect. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
